"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.OrcadDsnCfbStreamUtils = void 0;
const DsnCfbBinarySpec_1 = require("./DsnCfbBinarySpec");
const OrcadDsnCfbTypes_1 = require("./OrcadDsnCfbTypes");
var OrcadDsnCfbStreamUtils;
(function (OrcadDsnCfbStreamUtils) {
    OrcadDsnCfbStreamUtils.ROOT_PREFIX = 'Root Entry/';
    OrcadDsnCfbStreamUtils.RECORD_MAGIC = [0xff, 0xe4, 0x5c, 0x39];
    OrcadDsnCfbStreamUtils.RECORD_TERMINATOR = [0xff, 0xe4, 0x5c, 0x39, 0x00, 0x00, 0x00, 0x00];
    OrcadDsnCfbStreamUtils.LIBRARY_INSTANCE_MARKER_PATTERN = /^INS\d+(?:~\d*)?$/;
    function isLibraryInstanceMarker(value) {
        return OrcadDsnCfbStreamUtils.LIBRARY_INSTANCE_MARKER_PATTERN.test(value);
    }
    OrcadDsnCfbStreamUtils.isLibraryInstanceMarker = isLibraryInstanceMarker;
    OrcadDsnCfbStreamUtils.readUInt16LE = DsnCfbBinarySpec_1.DsnCfbBinarySpec.readUInt16LE;
    OrcadDsnCfbStreamUtils.readUInt32LE = DsnCfbBinarySpec_1.DsnCfbBinarySpec.readUInt32LE;
    OrcadDsnCfbStreamUtils.bytesToAscii = DsnCfbBinarySpec_1.DsnCfbBinarySpec.bytesToAscii;
    OrcadDsnCfbStreamUtils.isPrintableAsciiByte = DsnCfbBinarySpec_1.DsnCfbBinarySpec.isPrintableAsciiByte;
    OrcadDsnCfbStreamUtils.hasBytes = DsnCfbBinarySpec_1.DsnCfbBinarySpec.hasBytes;
    function readLengthPrefixedAsciiStringAt(bytes, offset, options = {}) {
        const maxLength = options.maxLength ?? 512;
        const requireNullTerminator = options.requireNullTerminator ?? true;
        if (offset + 2 > bytes.length) {
            return undefined;
        }
        const counted = DsnCfbBinarySpec_1.DsnCfbBinarySpec.readCountedAscii(bytes, offset, maxLength, requireNullTerminator);
        if (!counted) {
            return undefined;
        }
        const length = OrcadDsnCfbStreamUtils.readUInt16LE(bytes, offset);
        const hasNullTerminator = counted.byteLength === 2 + length + 1;
        return {
            offset,
            length,
            value: counted.value,
            hasNullTerminator,
        };
    }
    OrcadDsnCfbStreamUtils.readLengthPrefixedAsciiStringAt = readLengthPrefixedAsciiStringAt;
    function lengthPrefixedStringEnd(string) {
        return string.offset + 2 + string.length + (string.hasNullTerminator ? 1 : 0);
    }
    OrcadDsnCfbStreamUtils.lengthPrefixedStringEnd = lengthPrefixedStringEnd;
    function detectRecordHeader(bytes, offset = 0) {
        if (offset + 9 > bytes.length) {
            return undefined;
        }
        const payloadLength = OrcadDsnCfbStreamUtils.readUInt32LE(bytes, offset + 1);
        const totalLength = payloadLength + 9;
        if (payloadLength > bytes.length - offset - 9) {
            return undefined;
        }
        return {
            offset,
            type: bytes[offset],
            payloadLength,
            reserved: OrcadDsnCfbStreamUtils.readUInt32LE(bytes, offset + 5),
            totalLength,
        };
    }
    OrcadDsnCfbStreamUtils.detectRecordHeader = detectRecordHeader;
    function detectLeadingRecordHeaders(bytes, maxRecords = 32) {
        const records = [];
        let offset = 0;
        while (records.length < maxRecords) {
            const header = detectRecordHeader(bytes, offset);
            if (!header) {
                break;
            }
            records.push(header);
            if (header.totalLength <= 0) {
                break;
            }
            offset += header.totalLength;
        }
        return records;
    }
    OrcadDsnCfbStreamUtils.detectLeadingRecordHeaders = detectLeadingRecordHeaders;
    function readCountedAsciiField(bytes, offset) {
        if (offset + 2 > bytes.length) {
            return undefined;
        }
        const length = OrcadDsnCfbStreamUtils.readUInt16LE(bytes, offset);
        const valueStart = offset + 2;
        const valueEnd = valueStart + length;
        if (length > MAX_METADATA_FIELD_LENGTH || valueEnd >= bytes.length) {
            return undefined;
        }
        for (let index = valueStart; index < valueEnd; index++) {
            const byte = bytes[index];
            if (byte < 0x20 || byte > 0x7e) {
                return undefined;
            }
        }
        if (bytes[valueEnd] !== 0) {
            return undefined;
        }
        return { value: latin1String(bytes, valueStart, valueEnd), byteLength: 2 + length + 1 };
    }
    OrcadDsnCfbStreamUtils.readCountedAsciiField = readCountedAsciiField;
    const MAX_METADATA_FIELD_LENGTH = 512;
    const SHEET_SYMBOL_METADATA_FIELDS = 3;
    const CELL_VIEW_NAME_PATTERN = /\.(?:Normal|Convert|IEEE)$/i;
    function readCellViewHeader(bytes, offset) {
        const terminatorStart = offset - OrcadDsnCfbStreamUtils.RECORD_TERMINATOR.length;
        if (terminatorStart < 0) {
            return undefined;
        }
        for (let index = 0; index < OrcadDsnCfbStreamUtils.RECORD_TERMINATOR.length; index++) {
            if (bytes[terminatorStart + index] !== OrcadDsnCfbStreamUtils.RECORD_TERMINATOR[index]) {
                return undefined;
            }
        }
        const name = readCountedAsciiField(bytes, offset);
        if (!name || !CELL_VIEW_NAME_PATTERN.test(name.value)) {
            return undefined;
        }
        const library = readCountedAsciiField(bytes, offset + name.byteLength);
        return { value: name.value, byteLength: name.byteLength + (library?.byteLength ?? 0) };
    }
    function extractSheetSymbolMetadataRecords(bytes) {
        const records = [];
        let cellViewName;
        for (let offset = 0; offset + 2 <= bytes.length; offset++) {
            const header = readCellViewHeader(bytes, offset);
            if (header) {
                cellViewName = header.value;
                offset += header.byteLength - 1;
                continue;
            }
            const path = readCountedAsciiField(bytes, offset);
            if (!path || path.value.length === 0) {
                continue;
            }
            if (path.value.length < 5 || !/\.dsn$/i.test(path.value)) {
                continue;
            }
            const fields = [];
            let cursor = offset + path.byteLength;
            let malformed = false;
            for (let index = 0; index < SHEET_SYMBOL_METADATA_FIELDS; index++) {
                const field = malformed ? undefined : readCountedAsciiField(bytes, cursor);
                if (!field) {
                    malformed = true;
                    fields.push({ state: OrcadDsnCfbTypes_1.OrcadDsnCfbTypes.OrCadDsnCfbMetadataFieldState.malformed });
                    continue;
                }
                cursor += field.byteLength;
                fields.push(field.value.length === 0 ?
                    { state: OrcadDsnCfbTypes_1.OrcadDsnCfbTypes.OrCadDsnCfbMetadataFieldState.empty }
                    : { state: OrcadDsnCfbTypes_1.OrcadDsnCfbTypes.OrCadDsnCfbMetadataFieldState.stated, value: field.value });
            }
            records.push({
                offset,
                contentsLibName: path.value,
                ...(cellViewName ? { cellViewName } : {}),
                view: fields[0],
                symbolClass: fields[1],
                partValue: fields[2],
            });
            offset += path.byteLength - 1;
        }
        return records;
    }
    OrcadDsnCfbStreamUtils.extractSheetSymbolMetadataRecords = extractSheetSymbolMetadataRecords;
    function extractLengthPrefixedAsciiStrings(bytes, options = {}) {
        const minLength = options.minLength ?? 1;
        const maxLength = options.maxLength ?? 512;
        const maxCount = options.maxCount ?? 200;
        const requireNullTerminator = options.requireNullTerminator ?? true;
        const strings = [];
        for (let offset = 0; offset + 2 <= bytes.length && strings.length < maxCount; offset++) {
            const counted = DsnCfbBinarySpec_1.DsnCfbBinarySpec.readCountedAscii(bytes, offset, maxLength, requireNullTerminator);
            if (!counted) {
                continue;
            }
            const length = OrcadDsnCfbStreamUtils.readUInt16LE(bytes, offset);
            if (length < minLength) {
                continue;
            }
            const hasNullTerminator = counted.byteLength === 2 + length + 1;
            strings.push({
                offset,
                length,
                value: counted.value,
                hasNullTerminator,
                ...(offset >= 20 ?
                    {
                        precedingInt32Le20: DsnCfbBinarySpec_1.DsnCfbBinarySpec.readInt32LE(bytes, offset - 20),
                        precedingInt32Le16: DsnCfbBinarySpec_1.DsnCfbBinarySpec.readInt32LE(bytes, offset - 16),
                    }
                    : {}),
                ...(offset + counted.byteLength + 4 <= bytes.length ?
                    { followingUInt32Le: OrcadDsnCfbStreamUtils.readUInt32LE(bytes, offset + counted.byteLength) }
                    : {}),
            });
            offset += counted.byteLength - 1;
        }
        return strings;
    }
    OrcadDsnCfbStreamUtils.extractLengthPrefixedAsciiStrings = extractLengthPrefixedAsciiStrings;
    function latin1String(bytes, start, end) {
        const chunkSize = 0x8000;
        let value = '';
        for (let offset = start; offset < end; offset += chunkSize) {
            value += String.fromCharCode(...bytes.subarray(offset, Math.min(offset + chunkSize, end)));
        }
        return value;
    }
    function readContiguousCountedStrings(bytes, start, count) {
        const values = [];
        let offset = start;
        for (let index = 0; index < count; index++) {
            if (offset + 2 > bytes.length) {
                return undefined;
            }
            const length = OrcadDsnCfbStreamUtils.readUInt16LE(bytes, offset);
            const terminatorOffset = offset + 2 + length;
            if (terminatorOffset >= bytes.length || bytes[terminatorOffset] !== 0x00) {
                return undefined;
            }
            values.push(latin1String(bytes, offset + 2, terminatorOffset));
            offset = terminatorOffset + 1;
        }
        return { values, end: offset };
    }
    function computeFrameChainReachesWindow(bytes, windowStart) {
        const reachesWindow = new Uint8Array(bytes.length + 1);
        for (let offset = bytes.length; offset >= 0; offset--) {
            if (offset >= windowStart) {
                reachesWindow[offset] = 1;
                continue;
            }
            if (offset + 2 > bytes.length) {
                continue;
            }
            const length = OrcadDsnCfbStreamUtils.readUInt16LE(bytes, offset);
            const terminatorOffset = offset + 2 + length;
            if (terminatorOffset >= bytes.length || bytes[terminatorOffset] !== 0x00) {
                continue;
            }
            reachesWindow[offset] = reachesWindow[terminatorOffset + 1];
        }
        return reachesWindow;
    }
    function extractLibraryStringCatalog(bytes) {
        const maxTrailingBytes = 64;
        const windowStart = Math.max(0, bytes.length - maxTrailingBytes);
        const reachesWindow = computeFrameChainReachesWindow(bytes, windowStart);
        for (let offset = 0; offset + 4 <= bytes.length; offset++) {
            const count = OrcadDsnCfbStreamUtils.readUInt32LE(bytes, offset);
            if (count < 8 || count > 2_000_000) {
                continue;
            }
            if (!reachesWindow[offset + 4]) {
                continue;
            }
            const catalog = readContiguousCountedStrings(bytes, offset + 4, count);
            if (catalog && bytes.length - catalog.end <= maxTrailingBytes) {
                return catalog.values;
            }
        }
        return undefined;
    }
    OrcadDsnCfbStreamUtils.extractLibraryStringCatalog = extractLibraryStringCatalog;
    function extractAsciiRuns(bytes, options = {}) {
        const minLength = options.minLength ?? 4;
        const maxCount = options.maxCount ?? 200;
        return DsnCfbBinarySpec_1.DsnCfbBinarySpec.readAsciiRuns(bytes, minLength, maxCount);
    }
    OrcadDsnCfbStreamUtils.extractAsciiRuns = extractAsciiRuns;
    function forEachTerminatedPayload(bytes, callback) {
        let payloadOffset = 0;
        for (let offset = 0; offset <= bytes.length - OrcadDsnCfbStreamUtils.RECORD_TERMINATOR.length; offset++) {
            if (!OrcadDsnCfbStreamUtils.hasBytes(bytes, offset, OrcadDsnCfbStreamUtils.RECORD_TERMINATOR)) {
                continue;
            }
            callback({
                offset: payloadOffset,
                payloadByteLength: offset - payloadOffset,
                byteLength: offset + OrcadDsnCfbStreamUtils.RECORD_TERMINATOR.length - payloadOffset,
                payload: bytes.subarray(payloadOffset, offset),
            });
            payloadOffset = offset + OrcadDsnCfbStreamUtils.RECORD_TERMINATOR.length;
            offset = payloadOffset - 1;
        }
        if (payloadOffset < bytes.length) {
            callback({
                offset: payloadOffset,
                payloadByteLength: bytes.length - payloadOffset,
                byteLength: bytes.length - payloadOffset,
                payload: bytes.subarray(payloadOffset),
            });
        }
    }
    OrcadDsnCfbStreamUtils.forEachTerminatedPayload = forEachTerminatedPayload;
    function forEachPagePayload(bytes, callback) {
        let payloadOffset = 0;
        for (let offset = 0; offset <= bytes.length - OrcadDsnCfbStreamUtils.RECORD_MAGIC.length; offset++) {
            if (!OrcadDsnCfbStreamUtils.hasBytes(bytes, offset, OrcadDsnCfbStreamUtils.RECORD_MAGIC)) {
                continue;
            }
            callback({
                offset: payloadOffset,
                payloadByteLength: offset - payloadOffset,
                byteLength: offset + OrcadDsnCfbStreamUtils.RECORD_MAGIC.length - payloadOffset,
                payload: bytes.subarray(payloadOffset, offset),
            });
            const hasZeroTerminatorTail = offset + OrcadDsnCfbStreamUtils.RECORD_TERMINATOR.length <= bytes.length && OrcadDsnCfbStreamUtils.readUInt32LE(bytes, offset + 4) === 0;
            payloadOffset = offset + (hasZeroTerminatorTail ? OrcadDsnCfbStreamUtils.RECORD_TERMINATOR.length : OrcadDsnCfbStreamUtils.RECORD_MAGIC.length);
            offset = payloadOffset - 1;
        }
        if (payloadOffset < bytes.length) {
            callback({
                offset: payloadOffset,
                payloadByteLength: bytes.length - payloadOffset,
                byteLength: bytes.length - payloadOffset,
                payload: bytes.subarray(payloadOffset),
            });
        }
    }
    OrcadDsnCfbStreamUtils.forEachPagePayload = forEachPagePayload;
    function isDecodedObjectId(value) {
        return value !== undefined && Number.isInteger(value) && value > 0 && value < 0xffff_ffff;
    }
    OrcadDsnCfbStreamUtils.isDecodedObjectId = isDecodedObjectId;
    function stripRoot(path) {
        if (path === 'Root Entry/' || path === '/') {
            return '';
        }
        return path.startsWith(OrcadDsnCfbStreamUtils.ROOT_PREFIX) ? path.slice(OrcadDsnCfbStreamUtils.ROOT_PREFIX.length) : path;
    }
    OrcadDsnCfbStreamUtils.stripRoot = stripRoot;
})(OrcadDsnCfbStreamUtils || (exports.OrcadDsnCfbStreamUtils = OrcadDsnCfbStreamUtils = {}));
//# sourceMappingURL=OrcadDsnCfbStreamUtils.js.map